var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
/**
 * This component represents the entire panel which gets dropped down when the
 * user selects the component.  It encapsulates the search filter, the
 * Select-all item, and the list of options.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState, } from 'react';
import { useKey } from '../hooks/UseKey';
import { useMultiSelect } from '../hooks/UseMultiSelect';
import { KEY } from '../lib/constants';
import { debounce } from '../lib/debounce';
import { filterOptions } from '../lib/fuzzy-match-utils';
import { Cross } from './Cross';
import SelectItem from './SelectItem';
import SelectList from './SelectList';
var FocusType;
(function (FocusType) {
    FocusType[FocusType["SEARCH"] = 0] = "SEARCH";
    FocusType[FocusType["NONE"] = -1] = "NONE";
})(FocusType || (FocusType = {}));
var SelectPanel = function () {
    var _a = useMultiSelect(), t = _a.t, onChange = _a.onChange, options = _a.options, setOptions = _a.setOptions, value = _a.value, customFilterOptions = _a.filterOptions, selectAllLabel = _a.selectAllLabel, ItemRenderer = _a.ItemRenderer, disabled = _a.disabled, disableSearch = _a.disableSearch, hasSelectAll = _a.hasSelectAll, ClearIcon = _a.ClearIcon, debounceDuration = _a.debounceDuration, isCreatable = _a.isCreatable, onCreateOption = _a.onCreateOption;
    var listRef = useRef();
    var searchInputRef = useRef();
    var _b = useState(''), searchText = _b[0], setSearchText = _b[1];
    var _c = useState(options), filteredOptions = _c[0], setFilteredOptions = _c[1];
    var _d = useState(''), searchTextForFilter = _d[0], setSearchTextForFilter = _d[1];
    var _f = useState(0), focusIndex = _f[0], setFocusIndex = _f[1];
    var debouncedSearch = useCallback(debounce(function (query) { return setSearchTextForFilter(query); }, debounceDuration), []);
    var skipIndex = useMemo(function () {
        var start = 0;
        if (!disableSearch)
            start += 1; // if search is enabled then +1 to skipIndex
        if (hasSelectAll)
            start += 1; // if select-all is enabled then +1 to skipIndex
        return start;
    }, [disableSearch, hasSelectAll]);
    var selectAllOption = {
        label: selectAllLabel || t('selectAll'),
        value: '',
    };
    var selectAllValues = function (checked) {
        var filteredValues = filteredOptions
            .filter(function (o) { return !o.disabled; })
            .map(function (o) { return o.value; });
        if (checked) {
            var selectedValues = value.map(function (o) { return o.value; });
            var finalSelectedValues_1 = __spreadArray(__spreadArray([], selectedValues, true), filteredValues, true);
            return filteredOptions.filter(function (o) {
                return finalSelectedValues_1.includes(o.value);
            });
        }
        return value.filter(function (o) { return !filteredValues.includes(o.value); });
    };
    var selectAllChanged = function (checked) {
        var newOptions = selectAllValues(checked);
        onChange(newOptions);
    };
    var handleSearchChange = function (e) {
        debouncedSearch(e.target.value);
        setSearchText(e.target.value);
        setFocusIndex(FocusType.SEARCH);
    };
    var handleClear = function () {
        var _a;
        setSearchTextForFilter('');
        setSearchText('');
        (_a = searchInputRef === null || searchInputRef === void 0 ? void 0 : searchInputRef.current) === null || _a === void 0 ? void 0 : _a.focus();
    };
    var handleItemClicked = function (index) { return setFocusIndex(index); };
    // Arrow Key Navigation
    var handleKeyDown = function (e) {
        switch (e.code) {
            case KEY.ARROW_UP:
                updateFocus(-1);
                break;
            case KEY.ARROW_DOWN:
                updateFocus(1);
                break;
            default:
                return;
        }
        e.stopPropagation();
        e.preventDefault();
    };
    useKey([KEY.ARROW_DOWN, KEY.ARROW_UP], handleKeyDown, {
        target: listRef,
    });
    var handleSearchFocus = function () {
        setFocusIndex(FocusType.SEARCH);
    };
    var handleOnCreateOption = function () { return __awaiter(void 0, void 0, void 0, function () {
        var newOption;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    newOption = { label: searchText, value: searchText, __isNew__: true };
                    if (!onCreateOption) return [3 /*break*/, 2];
                    return [4 /*yield*/, onCreateOption(searchText)];
                case 1:
                    newOption = _a.sent();
                    _a.label = 2;
                case 2:
                    // adds created value to existing options
                    setOptions(__spreadArray([newOption], options, true));
                    handleClear();
                    onChange(__spreadArray(__spreadArray([], value, true), [newOption], false));
                    return [2 /*return*/];
            }
        });
    }); };
    var getFilteredOptions = function () { return __awaiter(void 0, void 0, void 0, function () {
        var _a;
        return __generator(this, function (_b) {
            switch (_b.label) {
                case 0:
                    if (!customFilterOptions) return [3 /*break*/, 2];
                    return [4 /*yield*/, customFilterOptions(options, searchTextForFilter)];
                case 1:
                    _a = _b.sent();
                    return [3 /*break*/, 3];
                case 2:
                    _a = filterOptions(options, searchTextForFilter);
                    _b.label = 3;
                case 3: return [2 /*return*/, _a];
            }
        });
    }); };
    var updateFocus = function (offset) {
        var newFocus = focusIndex + offset;
        newFocus = Math.max(0, newFocus);
        newFocus = Math.min(newFocus, options.length + Math.max(skipIndex - 1, 0));
        setFocusIndex(newFocus);
    };
    useEffect(function () {
        var _a, _b;
        (_b = (_a = listRef === null || listRef === void 0 ? void 0 : listRef.current) === null || _a === void 0 ? void 0 : _a.querySelector("[tabIndex='".concat(focusIndex, "']"))) === null || _b === void 0 ? void 0 : _b.focus();
    }, [focusIndex]);
    var _g = useMemo(function () {
        var filteredOptionsList = filteredOptions.filter(function (o) { return !o.disabled; });
        return [
            filteredOptionsList.every(function (o) { return value.findIndex(function (v) { return v.value === o.value; }) !== -1; }),
            filteredOptionsList.length !== 0,
        ];
        // eslint-disable-next-line
    }, [filteredOptions, value]), isAllOptionSelected = _g[0], hasSelectableOptions = _g[1];
    useEffect(function () {
        getFilteredOptions().then(setFilteredOptions);
    }, [searchTextForFilter, options]);
    return (React.createElement("div", { className: "select-panel", role: "listbox", ref: listRef },
        !disableSearch && (React.createElement("div", { className: "search" },
            React.createElement("input", { placeholder: t('search'), type: "text", "aria-describedby": t('search'), onChange: handleSearchChange, onFocus: handleSearchFocus, value: searchText, ref: searchInputRef, tabIndex: 0 }),
            React.createElement("button", { type: "button", className: "search-clear-button", hidden: !searchText, onClick: handleClear, "aria-label": t('clearSearch') }, ClearIcon || React.createElement(Cross, null)))),
        React.createElement("ul", { className: "options" },
            hasSelectAll && hasSelectableOptions && (React.createElement(SelectItem, { tabIndex: skipIndex === 1 ? 0 : 1, checked: isAllOptionSelected, option: selectAllOption, onSelectionChanged: selectAllChanged, onClick: function () { return handleItemClicked(1); }, itemRenderer: ItemRenderer, disabled: disabled })),
            filteredOptions.length ? (React.createElement(SelectList, { skipIndex: skipIndex, options: filteredOptions, onClick: function (_e, index) { return handleItemClicked(index); } })) : isCreatable ? (React.createElement("li", { onClick: handleOnCreateOption, className: "select-item creatable" },
                t('create'),
                " '",
                searchText,
                "'")) : (React.createElement("li", { className: "no-options" }, t('noOptions'))))));
};
export default SelectPanel;



//////////////////
// WEBPACK FOOTER
// ./src/components/MultiSelect/select-panel/index.tsx
// module id = 503
// module chunks = 0